/**
 * The door to /me (specs/me-account.md): an address, a six-digit code, in.
 *
 * Draws useOtpLogin in the page's own quiet style. With `join` (from a
 * /liity/<slug>?k=<code> link) the request carries the workshop and code, so
 * a person the organiser invited by link becomes a participant of that
 * workshop on their first sign-in. The reply never says whether the address
 * was known; the page says so too.
 */
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { ArrowRight, RotateCcw } from 'lucide-react';
import useOtpLogin from '../../hooks/useOtpLogin';
import { useTranslation } from '../../hooks/useTranslation';

const Card = styled.form`
  display: flex; flex-direction: column; gap: 12px; padding: 20px; border-radius: 14px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
`;
const Label = styled.label`font-size: 0.8rem; color: #9ca3af;`;
const Input = styled.input`
  width: 100%; padding: 12px 14px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.16);
  background: rgba(0,0,0,0.3); color: #e8e8ef; font: inherit; font-size: 1rem;
  &:focus { outline: none; border-color: #a78bfa; }
  &.code { font-family: ui-monospace, monospace; font-size: 1.6rem; letter-spacing: 0.4em; text-align: center; }
`;
const Row = styled.div`display: flex; gap: 8px; align-items: center; flex-wrap: wrap;`;
const Button = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 10px;
  border: 1px solid rgba(167,139,250,0.6); background: rgba(167,139,250,0.14); color: #e9d5ff;
  font: inherit; font-size: 0.92rem; cursor: pointer; &:disabled { opacity: 0.5; cursor: default; }
`;
const Quiet = styled.button`
  background: none; border: none; color: #9ca3af; font: inherit; font-size: 0.82rem; cursor: pointer;
  display: inline-flex; align-items: center; gap: 5px; padding: 6px 4px; &:hover { color: #e8e8ef; } &:disabled { opacity: 0.5; cursor: default; }
`;
const Note = styled.p`margin: 0; font-size: 0.84rem; color: #9ca3af; line-height: 1.5;`;
const Err = styled.p`margin: 0; font-size: 0.84rem; color: #ff8a85; line-height: 1.5;`;
const Ok = styled.p`margin: 0; font-size: 0.84rem; color: #3ecf8e;`;

export default function MeSignIn({ join = null, initialEmail = '', onSignedIn }) {
  const { t } = useTranslation();
  const otp = useOtpLogin({
    initialEmail,
    onSuccess: (data) => onSignedIn?.(data),
    extra: join ? { join: { slug: join.slug, code: join.code } } : undefined,
    resendNotice: t('me.door.resent'),
    successNotice: t('me.door.signingIn'),
  });
  const codeRef = useRef(null);
  useEffect(() => { if (otp.step === 'code') codeRef.current?.focus(); }, [otp.step]);

  if (otp.step === 'code') {
    return (
      <Card onSubmit={(e) => { e.preventDefault(); otp.verifyCode(); }}>
        <Note>{t('me.door.codeSent', { email: otp.email })}</Note>
        <Label htmlFor="me-code">{t('me.door.codeLabel')}</Label>
        <Input id="me-code" ref={codeRef} className="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*"
          value={otp.code} onChange={(e) => otp.setCode(e.target.value)} disabled={otp.busy} aria-invalid={!!otp.error} />
        {otp.error && <Err>{otp.error}</Err>}
        {otp.notice && <Ok>{otp.notice}</Ok>}
        <Row>
          <Button type="submit" disabled={otp.busy || otp.code.length < 6}>{t('me.door.verify')} <ArrowRight size={15} /></Button>
          <Quiet type="button" onClick={() => otp.requestCode(true)} disabled={otp.busy || otp.cooldown > 0}>
            <RotateCcw size={13} /> {otp.cooldown > 0 ? t('me.door.resendIn', { seconds: otp.cooldown }) : t('me.door.resend')}
          </Quiet>
          <Quiet type="button" onClick={otp.changeEmail} disabled={otp.busy}>{t('me.door.changeEmail')}</Quiet>
        </Row>
      </Card>
    );
  }

  return (
    <Card onSubmit={(e) => { e.preventDefault(); otp.requestCode(); }}>
      {join && <Note>{join.title ? t('me.door.joining', { title: join.title }) : t('me.door.joiningUnknown')}</Note>}
      {join && join.hasJoinCode === false && <Err>{t('me.door.noJoinCode')}</Err>}
      <Label htmlFor="me-email">{t('me.door.emailLabel')}</Label>
      <Input id="me-email" type="email" autoComplete="email" inputMode="email" autoFocus placeholder={t('me.door.emailPlaceholder')}
        value={otp.email} onChange={(e) => otp.setEmail(e.target.value)} disabled={otp.busy} />
      {otp.error && <Err>{otp.error}</Err>}
      <Row>
        <Button type="submit" disabled={otp.busy || !otp.email.includes('@')}>{t('me.door.sendCode')} <ArrowRight size={15} /></Button>
      </Row>
      <Note>{join ? t('me.door.howJoin') : t('me.signInInvited')}</Note>
    </Card>
  );
}
